/**
 * Owner: Masa Bayu
 * File: build-justification-data.ts
 * Date:Time: 2026-10-05 12:24
 * Description: Fasa 6–8 · adapter · normalizeTokenData 0.25em · tokenize.
 */

import {
  distributeSpacing,
  generateRenderInstructions,
  optimizeBreakpoints,
  type RenderInstruction,
  type TextToken,
} from '@qx/spsu-line-break-optimizer';
import {
  groupNativeLinesIntoParagraphs,
  isEngineParagraphGroup,
  type NativeLine,
} from './group-native-paragraphs';
import {
  detectNativeDropCapPairs,
  type NativeDropCapPair,
} from './spsu-native-dropcap-face';
import type { TextElement } from '../extract/spsu-types';

export type { NativeLine };
export { groupNativeLinesIntoParagraphs, isEngineParagraphGroup };

/** Alias kontrak Owner · plat teks SPSU sahaja. */
export type NativeElement = TextElement;

export type JustificationPlan = {
  readonly dropcap: TextElement | null;
  readonly flowPlates: readonly TextElement[];
  readonly tokens: readonly TextToken[];
  /** Baris 1 sempit (dropcap); baris 2+ guna lineWidth penuh. */
  readonly constraints: readonly number[];
  readonly lineWidth: number;
  readonly instructions: readonly RenderInstruction[];
};

/** Tipografi standard · ruang perkataan ≈ 25% em (saiz fon plat, unit pt). */
const SPACE_AFTER_EM = 0.25;
const STRETCH_OF_SPACE = 0.5;
const SHRINK_OF_SPACE = 0.2;

/** Input mentah PoC / SPSU ringkas sebelum TextToken penuh. */
export type RawTokenElement = {
  readonly id: string;
  readonly text: string;
  readonly x?: number;
  readonly y?: number;
  readonly width: number;
  readonly fontSize: number;
  readonly spaceAfter?: number;
};

/**
 * Peringkat 1 · Normalisasi Data.
 * spaceAfter 0 / missing → 0.25em tipografi · racun glued dihentikan di hulu.
 */
export function normalizeTokenData(
  rawElements: readonly RawTokenElement[],
): TextToken[] {
  return rawElements.map((el) => {
    const fontSize = el.fontSize > 0 ? el.fontSize : 11;
    const baseSpace = fontSize * SPACE_AFTER_EM;
    const spaceAfter =
      el.spaceAfter != null && el.spaceAfter > 0 ? el.spaceAfter : baseSpace;
    return {
      id: el.id,
      text: el.text,
      width: el.width,
      spaceAfter,
      stretch: baseSpace * STRETCH_OF_SPACE,
      shrink: baseSpace * SHRINK_OF_SPACE,
    };
  });
}

function bodyStyle(fontSize: number): TextElement['style'] {
  return {
    fontFamily: 'serif',
    fontSize,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#000000',
    textAlign: 'left',
  };
}

/** Eksport helper ujian · bina TextElement mock ringan. */
export function mockTextPlate(input: {
  readonly id: string;
  readonly content: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly fontSize: number;
  readonly height?: number;
}): TextElement {
  return {
    id: input.id,
    type: 'text',
    content: input.content,
    bbox: {
      x: input.x,
      y: input.y,
      width: input.width,
      height: input.height ?? input.fontSize * 1.2,
      unit: 'pt',
    },
    layer: 0,
    style: bodyStyle(input.fontSize),
  };
}

/**
 * Pecah plat absolute kepada TextToken (perkataan).
 * Lebar perkataan dianggar berkadar panjang aksara dalam plat.
 * spaceAfter = 0.25em tipografi · bukan 0 (DEFAULT_SPACE cacat → margin ketat).
 */
export function tokenizeFlowPlates(plates: readonly TextElement[]): TextToken[] {
  const tokens: TextToken[] = [];
  for (let p = 0; p < plates.length; p += 1) {
    const plate = plates[p]!;
    const words = plate.content.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    const totalChars = words.reduce((sum, w) => sum + Math.max(1, w.length), 0);
    const fontSize =
      plate.style.fontSize > 0 ? plate.style.fontSize : plate.bbox.height;
    const baseSpaceAfter = fontSize * SPACE_AFTER_EM;
    for (let i = 0; i < words.length; i += 1) {
      const word = words[i]!;
      const width = (plate.bbox.width * Math.max(1, word.length)) / totalChars;
      const isLastInPlate = i === words.length - 1;
      const isLastPlate = p === plates.length - 1;
      const isTerminal = isLastInPlate && isLastPlate;
      tokens.push({
        id: `${plate.id}:${i}`,
        text: word,
        width,
        spaceAfter: isTerminal ? 0 : baseSpaceAfter,
        stretch: baseSpaceAfter * STRETCH_OF_SPACE,
        shrink: baseSpaceAfter * SHRINK_OF_SPACE,
      });
    }
  }
  return tokens;
}

function line1AvailableWidth(
  dropcap: TextElement,
  host: TextElement,
  containerWidth: number,
): number {
  const columnLeft = Math.min(dropcap.bbox.x, host.bbox.x);
  const inset = host.bbox.x - columnLeft;
  return Math.max(1, containerWidth - inset);
}

/** Breakpoint = indeks token terakhir setiap plat aliran (satu baris Face = satu plat). */
function plateAlignedBreakpoints(plates: readonly TextElement[]): number[] {
  const breakpoints: number[] = [];
  let tokenIndex = -1;
  for (const plate of plates) {
    const words = plate.content.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    tokenIndex += words.length;
    breakpoints.push(tokenIndex);
  }
  return breakpoints;
}

function planPlainParagraph(
  plates: readonly TextElement[],
  containerWidth: number,
): JustificationPlan {
  const tokens = tokenizeFlowPlates(plates);
  const breakpoints = plateAlignedBreakpoints(plates);
  if (breakpoints.length === 0) {
    return {
      dropcap: null,
      flowPlates: plates,
      tokens,
      constraints: [],
      lineWidth: containerWidth,
      instructions: [],
    };
  }
  const constraints = breakpoints.map(() => containerWidth);
  const adjustments = distributeSpacing({
    tokens,
    breakpoints,
    constraints: [...constraints],
    lineWidth: containerWidth,
  });
  return {
    dropcap: null,
    flowPlates: plates,
    tokens,
    constraints,
    lineWidth: containerWidth,
    instructions: generateRenderInstructions(tokens, adjustments, breakpoints),
  };
}

function planDropcapParagraph(
  texts: readonly TextElement[],
  pair: NonNullable<ReturnType<typeof detectNativeDropCapPairs>[0]>,
  containerWidth: number,
): JustificationPlan {
  const flowPlates = [
    pair.host,
    ...pair.absorbIds
      .map((id) => texts.find((t) => t.id === id))
      .filter((el): el is TextElement => el != null),
  ];
  // Sertakan baris body susulan dalam kumpulan yang sama (bukan absorb dropcap).
  const flowIds = new Set(flowPlates.map((p) => p.id));
  for (const t of texts) {
    if (t.id === pair.letter.id || flowIds.has(t.id)) continue;
    if (Math.abs(t.style.fontSize - pair.host.style.fontSize) > 0.5) continue;
    const atHost = Math.abs(t.bbox.x - pair.host.bbox.x) <= 2;
    const atColumn = Math.abs(t.bbox.x - pair.letter.bbox.x) <= 2;
    if (!atHost && !atColumn) continue;
    if (t.bbox.y <= flowPlates[flowPlates.length - 1]!.bbox.y) continue;
    flowPlates.push(t);
    flowIds.add(t.id);
  }
  flowPlates.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);

  const tokens = tokenizeFlowPlates(flowPlates);
  const narrow = line1AvailableWidth(pair.letter, pair.host, containerWidth);
  const wrapBeside = 1 + pair.absorbIds.length;
  const constraints = flowPlates.map((_, i) =>
    i < wrapBeside ? narrow : containerWidth,
  );
  const lineWidth =
    flowPlates.length <= wrapBeside ? narrow : containerWidth;
  const breakpoints = plateAlignedBreakpoints(flowPlates);
  const adjustments = distributeSpacing({
    tokens,
    breakpoints,
    constraints: [...constraints],
    lineWidth,
  });
  return {
    dropcap: pair.letter,
    flowPlates,
    tokens,
    constraints,
    lineWidth,
    instructions: generateRenderInstructions(tokens, adjustments, breakpoints),
  };
}

/**
 * Rancang justifikasi satu kumpulan plat · dropcap atau body biasa.
 * Pure · tiada DOM / React.
 */
export function buildJustificationPlan(
  elements: readonly NativeElement[],
  containerWidth: number,
): JustificationPlan {
  const texts = elements.filter((el): el is TextElement => el.type === 'text');
  const pairs = detectNativeDropCapPairs(texts);
  const pair = pairs[0] ?? null;

  if (!pair) {
    if (texts.length > 1) {
      return planPlainParagraph(texts, containerWidth);
    }
    const tokens = tokenizeFlowPlates(texts);
    const optimized = optimizeBreakpoints({
      tokens,
      constraints: [],
      lineWidth: containerWidth,
    });
    const adjustments = distributeSpacing({
      tokens,
      breakpoints: optimized.breakpoints,
      constraints: [],
      lineWidth: containerWidth,
    });
    return {
      dropcap: null,
      flowPlates: texts,
      tokens,
      constraints: [],
      lineWidth: containerWidth,
      instructions: generateRenderInstructions(
        tokens,
        adjustments,
        optimized.breakpoints,
      ),
    };
  }

  return planDropcapParagraph(texts, pair, containerWidth);
}

export type ParagraphJustificationPlan = JustificationPlan & {
  readonly plates: readonly TextElement[];
  readonly groupIndex: number;
};

/**
 * Fasa 8 · setiap kumpulan >1 baris → satu plan Core Engine.
 */
export function buildAllJustificationPlans(
  elements: readonly NativeElement[],
  containerWidth: number,
): ParagraphJustificationPlan[] {
  const texts = elements.filter((el): el is TextElement => el.type === 'text');
  const groups = groupNativeLinesIntoParagraphs(texts);
  const plans: ParagraphJustificationPlan[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i]!;
    if (!isEngineParagraphGroup(group)) continue;
    const columnLeft = Math.min(...group.map((g) => g.bbox.x));
    const columnRight = Math.max(...group.map((g) => g.bbox.x + g.bbox.width));
    const width = Math.max(1, columnRight - columnLeft);
    const plan = buildJustificationPlan(group, Math.max(containerWidth, width));
    if (plan.instructions.length === 0) continue;
    plans.push({ ...plan, plates: group, groupIndex: i });
  }
  return plans;
}

/**
 * Adapter · SPSU NativeElement[] → RenderInstruction[] (kumpulan pertama / dropcap).
 */
export function buildJustificationData(
  elements: readonly NativeElement[],
  containerWidth: number,
): RenderInstruction[] {
  const plans = buildAllJustificationPlans(elements, containerWidth);
  if (plans.length > 0) return [...plans[0]!.instructions];
  return [...buildJustificationPlan(elements, containerWidth).instructions];
}

/**
 * Anggar lebar lajur penuh dari plat body sehalaman (untuk Face injection).
 */
export function estimateColumnWidth(
  texts: readonly TextElement[],
  pair: NativeDropCapPair,
  columnLeft: number,
): number {
  let right = pair.host.bbox.x + pair.host.bbox.width;
  const bodySize = pair.host.style.fontSize;
  for (const t of texts) {
    if (Math.abs(t.style.fontSize - bodySize) > 1.5) continue;
    right = Math.max(right, t.bbox.x + t.bbox.width);
  }
  return Math.max(pair.host.bbox.width, right - columnLeft);
}
